import { Children, cloneElement, isValidElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  ListChecks,
  Network,
  ShieldCheck,
  Wallet,
  XCircle,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, LimitBar, PageHeader, Skeleton, cx } from "../components/ui";
import { PILLARS } from "../app/navigation";
import { isAccountAdmin } from "../lib/people";
import { formatBytes, formatNumber } from "../lib/format";

/**
 * Settings is the **configuration hub**, not a second copy of the operational
 * screens. It owns company defaults and plan/usage, and it *points at* the one
 * screen that owns each operational setting (Organisation, Leave, Roster,
 * Salary, Payroll) rather than re-implementing their CRUD. One owner per
 * setting; no duplication.
 */
export function SettingsPage() {
  const { user } = useAuth();
  const account = user?.account;
  const canEdit = isAccountAdmin(user?.role);
  const accountId = account?.id ?? "";

  const usage = useQuery({
    queryKey: ["usage", account?.id],
    queryFn: ({ signal }) => api.usage(accountId, signal),
    enabled: accountId !== "",
  });

  if (!account) return null;

  const MODULE_LABEL: Record<string, string> = {
    customers: "Customers",
    billing: "Billing",
    people: "People",
    automation: "Automation",
  };

  return (
    <>
      <PageHeader title="Settings" subtitle={`${account.name} · ${account.slug}`} />

      <div className="grid gap-6 lg:grid-cols-2">
        <CompanyCard accountId={accountId} canEdit={canEdit} />

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Plan &amp; usage</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              {usage.data?.plan?.name ?? "—"}
            </span>
          </div>

          {usage.isPending ? (
            <div className="space-y-4">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : usage.data ? (
            <div className="space-y-5">
              <LimitBar
                label={`${account.primary_object_label ?? "Contacts"} (logins are counted separately)`}
                value={usage.data.usage.contacts.current}
                limit={usage.data.usage.contacts.limit}
                format={formatNumber}
              />
              <LimitBar
                label="Logins"
                value={usage.data.usage.users.current}
                limit={usage.data.usage.users.limit}
                format={formatNumber}
              />
              <LimitBar
                label="Employees"
                value={usage.data.usage.employees.current}
                limit={usage.data.usage.employees.limit}
                format={formatNumber}
              />
              <LimitBar
                label="Storage"
                value={usage.data.usage.storage_bytes.current}
                limit={usage.data.usage.storage_bytes.limit}
                format={formatBytes}
              />
            </div>
          ) : null}
        </Card>

        <ConfigOwnersCard />

        <PermissionsCard />

        <Card className="lg:col-span-2">
          <h2 className="mb-1 text-sm font-semibold text-slate-900">Modules</h2>
          <p className="mb-4 text-xs text-slate-500">
            What this plan unlocks. A locked module is a plan limit, not a
            permission — it means upgrade, not "ask an admin".
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {PILLARS.filter((p) => p.moduleCode).map((pillar) => {
              const enabled = usage.data?.modules.includes(pillar.moduleCode!) ?? false;
              return (
                <li key={pillar.id} className="flex items-start gap-2.5">
                  {enabled ? (
                    <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle size={17} className="mt-0.5 shrink-0 text-slate-300" />
                  )}
                  <div className="min-w-0">
                    <p
                      className={cx(
                        "text-sm font-medium",
                        enabled ? "text-slate-900" : "text-slate-400",
                      )}
                    >
                      {MODULE_LABEL[pillar.moduleCode!] ?? pillar.label}
                    </p>
                    <p className="text-xs text-slate-500">{pillar.blurb}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </>
  );
}

const WEEK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DATE_FORMATS = ["MMM D, YYYY", "DD/MM/YYYY", "DD MMM YYYY", "MM/DD/YYYY", "YYYY-MM-DD"];
const COMMON_TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
  "UTC",
];

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900 disabled:bg-slate-50 disabled:text-slate-500";
const invalidInputClass = "ring-rose-400 focus:ring-rose-400";

/** The API's field-level errors, keyed by request field name. */
export function fieldErrorsFrom(error: unknown): Record<string, string[]> {
  return error instanceof ApiError ? error.fields : {};
}

function CompanyCard({ accountId, canEdit }: { accountId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const account = useQuery({
    queryKey: ["account", accountId],
    queryFn: ({ signal }) => api.account(accountId, signal),
    enabled: accountId !== "",
  });

  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.updateAccount>[1]) =>
      api.updateAccount(accountId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    },
  });

  const fieldErrors = fieldErrorsFrom(save.error);
  const formErrors = [
    ...(fieldErrors.non_field_errors ?? []),
    ...(fieldErrors.detail ?? []),
  ];
  // Keep a value the server already accepted selectable, even if it is not one
  // of the presets, so re-saving without touching it cannot blank the field.
  const dateFormats =
    account.data?.date_format && !DATE_FORMATS.includes(account.data.date_format)
      ? [account.data.date_format, ...DATE_FORMATS]
      : DATE_FORMATS;

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Company</h2>
        {!canEdit ? (
          <span className="text-xs text-slate-400">Owner / admin only</span>
        ) : null}
      </div>

      {account.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : account.isError ? (
        <p className="text-sm text-rose-600">Could not load company settings.</p>
      ) : (
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit) return;
            const data = new FormData(event.currentTarget);
            save.mutate({
              name: String(data.get("name") ?? "").trim(),
              legal_name: String(data.get("legal_name") ?? "").trim(),
              phone: String(data.get("phone") ?? "").trim(),
              website: String(data.get("website") ?? "").trim(),
              billing_email: String(data.get("billing_email") ?? "").trim(),
              tax_identifier: String(data.get("tax_identifier") ?? "").trim(),
              address_line1: String(data.get("address_line1") ?? "").trim(),
              address_line2: String(data.get("address_line2") ?? "").trim(),
              city: String(data.get("city") ?? "").trim(),
              state: String(data.get("state") ?? "").trim(),
              postal_code: String(data.get("postal_code") ?? "").trim(),
              country_code: String(data.get("country_code") ?? "").trim().toUpperCase(),
              timezone: String(data.get("timezone") ?? ""),
              week_start: Number(data.get("week_start") ?? 1),
              date_format: String(data.get("date_format") ?? ""),
            });
          }}
        >
          <Field label="Business name" errors={fieldErrors.name}>
            <input name="name" defaultValue={account.data?.name ?? ""} required disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Legal name" errors={fieldErrors.legal_name}>
            <input name="legal_name" defaultValue={account.data?.legal_name ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Phone" errors={fieldErrors.phone}>
            <input name="phone" defaultValue={account.data?.phone ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Website" errors={fieldErrors.website}>
            <input name="website" defaultValue={account.data?.website ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Billing email" errors={fieldErrors.billing_email}>
            <input name="billing_email" defaultValue={account.data?.billing_email ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Tax identifier" hint="GSTIN in India. Printed on your invoices as the seller's identifier when set." errors={fieldErrors.tax_identifier}>
            <input name="tax_identifier" defaultValue={account.data?.tax_identifier ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Address line 1" errors={fieldErrors.address_line1}>
            <input name="address_line1" defaultValue={account.data?.address_line1 ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Address line 2" errors={fieldErrors.address_line2}>
            <input name="address_line2" defaultValue={account.data?.address_line2 ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="City" errors={fieldErrors.city}>
            <input name="city" defaultValue={account.data?.city ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="State" errors={fieldErrors.state}>
            <input name="state" defaultValue={account.data?.state ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Postal code" errors={fieldErrors.postal_code}>
            <input name="postal_code" defaultValue={account.data?.postal_code ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Country code" hint="Two-letter ISO code, e.g. IN." errors={fieldErrors.country_code}>
            <input name="country_code" defaultValue={account.data?.country_code ?? ""} disabled={!canEdit} className={inputClass} />
          </Field>
          <Field label="Timezone" hint="IANA name. All timestamps are UTC on the wire and render in this zone." errors={fieldErrors.timezone}>
            <input
              name="timezone"
              list="timezone-options"
              defaultValue={account.data?.timezone ?? "Asia/Kolkata"}
              disabled={!canEdit}
              className={inputClass}
            />
            <datalist id="timezone-options">
              {COMMON_TIMEZONES.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </Field>
          <Field label="Week starts on" errors={fieldErrors.week_start}>
            <select
              name="week_start"
              defaultValue={String(account.data?.week_start ?? 1)}
              disabled={!canEdit}
              className={inputClass}
            >
              {WEEK_DAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Date format" errors={fieldErrors.date_format}>
            <select
              name="date_format"
              defaultValue={account.data?.date_format ?? "DD/MM/YYYY"}
              disabled={!canEdit}
              className={inputClass}
            >
              {dateFormats.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </Field>

          {canEdit ? (
            <div className="flex items-center justify-end gap-3 sm:col-span-2">
              {save.isSuccess ? <span className="text-sm text-emerald-600">Saved.</span> : null}
              {save.error instanceof ApiError ? (
                <span role="alert" className="text-sm text-rose-600">
                  {formErrors.length ? formErrors.join(" ") : save.error.message}
                </span>
              ) : null}
              <Button type="submit" variant="primary" disabled={save.isPending}>
                Save changes
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Card>
  );
}

const CONFIG_OWNERS: Array<{
  label: string;
  owner: string;
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  roles: string;
}> = [
  { label: "Branches, departments & designations", owner: "Owned by Organisation", to: "/people/organization", icon: Network, roles: "Owner, Manager" },
  { label: "Leave types, entitlements & accrual", owner: "Owned by Leave", to: "/people/leave", icon: ClipboardList, roles: "Owner" },
  { label: "Shift templates & breaks", owner: "Owned by Roster", to: "/people/roster", icon: CalendarDays, roles: "Owner, Manager" },
  { label: "Attendance & regularisation", owner: "Owned by Attendance", to: "/people/attendance", icon: ListChecks, roles: "Owner, Manager" },
  { label: "Salary components & structures", owner: "Owned by Salary", to: "/people/salary", icon: BadgeDollarSign, roles: "Owner" },
  { label: "Payroll periods & approvals", owner: "Owned by Payroll", to: "/people/payroll", icon: Wallet, roles: "Owner" },
];

function ConfigOwnersCard() {
  return (
    <Card className="lg:col-span-2">
      <h2 className="mb-1 text-sm font-semibold text-slate-900">Configuration</h2>
      <p className="mb-4 text-xs text-slate-500">
        Each setting has one home. Settings links to it rather than duplicating it.
      </p>
      <ul className="divide-y divide-slate-100">
        {CONFIG_OWNERS.map((row) => (
          <li key={row.to}>
            <Link
              to={row.to}
              className="-mx-2 flex items-center gap-3 rounded-md px-2 py-3 hover:bg-slate-50"
            >
              <row.icon size={17} className="shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-800">{row.label}</span>
                <span className="block text-xs text-slate-500">
                  {row.owner} · visible to {row.roles}
                </span>
              </span>
              <ArrowRight size={15} className="shrink-0 text-slate-300" />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function PermissionsCard() {
  return (
    <Card className="lg:col-span-2">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-900">
        <ShieldCheck size={16} className="text-slate-500" /> Roles &amp; permissions
      </h2>
      <div className="space-y-2 text-sm text-slate-600">
        <p>
          There are two account types: <span className="font-medium text-slate-800">Owner</span> and{" "}
          <span className="font-medium text-slate-800">Employee</span>. A manager is an employee
          with at least one direct report — not a separate account type.
        </p>
        <p>
          The workspace a person gets is derived server-side from their identity and the persisted
          reporting relationship, and enforced on the API. An employee sees only themselves; a
          manager sees their direct reports; an owner sees the company. Broader reporting-tree
          access is an explicit, off-by-default authorisation.
        </p>
        <p className="text-xs text-slate-400">
          Changing who reports to whom happens on the employee record (Reports To) under Employees.
        </p>
      </div>
    </Card>
  );
}

export function Field({
  label,
  hint,
  errors,
  children,
}: {
  label: string;
  hint?: string;
  errors?: string[] | undefined;
  children: React.ReactNode;
}) {
  const invalid = Boolean(errors?.length);
  const marked = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type !== "input" && child.type !== "select") return child;
    const props = child.props as { className?: string };
    return cloneElement(
      child as React.ReactElement<{ className?: string; "aria-invalid"?: boolean }>,
      {
        "aria-invalid": invalid ? true : undefined,
        className: cx(props.className, invalid && invalidInputClass),
      },
    );
  });

  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {marked}
      {invalid ? (
        <span role="alert" className="mt-1 block text-sm text-rose-600">
          {errors?.join(" ")}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-slate-400">{hint}</span>
      ) : null}
    </label>
  );
}
